'use client';

import {safeLocalStorage} from '@utils/safeStorages';
import {useEffect, useRef, useState} from 'react';

import {SNACKBAR_SHOW_TIME} from '@web-catalog/const/numbers';

import {useOmicronClientFlag} from '@web-catalog/hooks/omicron';

import {
  useAuthorizedProfile,
  useSetAuthProgressState,
} from '@web-catalog/store/auth';

import {SpriteIcon} from '@web-catalog/ui-kit/SpriteIcon';

import {
  logAuthorizationBannerShow,
  logAuthorizationBannerUserAgreementClick,
  logAuthorizationDone,
} from '@web-catalog/utils/analytics/authAnalytics';

import {AUTH_SHOWN_STORAGE_KEY} from '../consts';
import styles from './AuthSnackBar.module.scss';
import {Avatar} from './Avatar';

const animationDurationMs = 300;
const enterAnimationDelayMs = 1000;

export const AuthSnackBar = () => {
  const snackbarRef = useRef<HTMLDivElement>(null);
  const webAvailableAutoLoginType = useOmicronClientFlag('WEB_AUTO_LOGIN');
  const userInfo = useAuthorizedProfile();
  const setAuthProgress = useSetAuthProgressState();
  const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);

  const isAuthDisabled = webAvailableAutoLoginType === 'none';

  useEffect(() => {
    if (isAuthDisabled || !userInfo?.id) return;

    const authSnackBarShown = safeLocalStorage.getItem(AUTH_SHOWN_STORAGE_KEY);

    if (!authSnackBarShown) {
      setIsSnackBarVisible(true);
      logAuthorizationBannerShow();
      logAuthorizationDone();
      safeLocalStorage.setItem(AUTH_SHOWN_STORAGE_KEY, 'shown');
    } else {
      setAuthProgress('finished');
    }
  }, [isAuthDisabled, userInfo, setAuthProgress]);

  useEffect(() => {
    if (isAuthDisabled || !isSnackBarVisible) return;

    const finishDisappearTimer = setTimeout(
      () => setIsSnackBarVisible(false),
      SNACKBAR_SHOW_TIME + animationDurationMs * 2 + enterAnimationDelayMs,
    );

    const startDisappearTimer = setTimeout(
      () => {
        setAuthProgress('finished');
        const view = snackbarRef.current;
        if (view) {
          view.classList.add(styles.leaving);
        } else {
          clearTimeout(finishDisappearTimer);
          setIsSnackBarVisible(false);
        }
      },
      SNACKBAR_SHOW_TIME + animationDurationMs + enterAnimationDelayMs,
    );
    return () => {
      clearTimeout(startDisappearTimer);
      clearTimeout(finishDisappearTimer);
    };
  }, [isAuthDisabled, isSnackBarVisible, setAuthProgress]);

  if (!isSnackBarVisible || !userInfo?.id) {
    return null;
  }

  return (
    <div
      data-testid="auth-snack"
      className={styles.container}
      ref={snackbarRef}
    >
      <Avatar className={styles.avatar} />

      <div>
        <div data-testid="name" className={styles.name}>{`Привет, ${
          userInfo?.first_name ?? userInfo?.id
        }!`}</div>
        <div data-testid="id" className={styles.id}>
          Вы вошли с помощью <span className="visually-hidden">VK ID</span>
          <SpriteIcon svg="vk_id" color="white" />
        </div>
        <a
          href={'https://www.rustore.ru/help/legal/terms-of-use'}
          onClick={logAuthorizationBannerUserAgreementClick}
          data-testid="terms-link"
        >
          Пользовательское соглашение RuStore
        </a>
      </div>
    </div>
  );
};
